// src/services/communicationService.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Get the auth token from localStorage
const getAuthToken = () => localStorage.getItem('token');

// Set the Authorization header with the token
const getHeaders = () => ({
  headers: {
    Authorization: `Bearer ${getAuthToken()}`
  }
});

// Get communication history for a lead
export const getLeadCommunicationHistory = async (leadId) => {
  try {
    const response = await axios.get(
      `${API_URL}/communications/leads/${leadId}/history`, 
      getHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching communication history:', error);
    throw error;
  }
};

// Make an outbound call
export const makeCall = async (toNumber, leadId, notes = '') => {
  try {
    const response = await axios.post(
      `${API_URL}/communications/call`, 
      { to_number: toNumber, lead_id: leadId, notes },
      getHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('Error making call:', error);
    throw error;
  }
};

// NEW: Initiate click-to-call (user's phone rings first, then connects to customer)
export const initiateClickToCall = async (callData) => {
  try {
    // Validate required fields
    if (!callData.to_number || !callData.callback_number) {
      throw new Error('Both customer phone number and your phone number are required');
    }

    // Format phone numbers to ensure they have country code
    const formattedData = {
      ...callData,
      to_number: formatPhoneNumber(callData.to_number),
      callback_number: formatPhoneNumber(callData.callback_number),
    };

    const response = await axios.post(
      `${API_URL}/communications/click-to-call`,
      formattedData,
      {
        headers: {
          ...getHeaders().headers,
          'Content-Type': 'application/json',
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error initiating click-to-call:', error);
    
    // Handle different types of errors
    if (error.response) {
      // Server responded with error status
      const errorMessage = error.response.data?.message || error.response.data?.error || 'Server error occurred';
      throw new Error(errorMessage);
    } else if (error.request) {
      // Request was made but no response
      throw new Error('Unable to connect to the server. Please check your internet connection.');
    } else {
      // Something else happened
      throw new Error(error.message || 'An unexpected error occurred');
    }
  }
};

// Direct click-to-call from a pre-saved URL. Uses the user's persistent
// direct_call_key in the body as the credential (no Authorization header).
export const initiateDirectCall = async ({ to_number, callback_number, lead_id, notes, k }) => {
  if (!to_number || !callback_number) {
    throw new Error('Both customer phone number and your phone number are required');
  }
  if (!lead_id) {
    throw new Error('leadId is required');
  }
  if (!k) {
    throw new Error('Direct-call key is required');
  }

  const payload = {
    to_number: formatPhoneNumber(to_number),
    callback_number: formatPhoneNumber(callback_number),
    lead_id,
    notes,
    k
  };

  try {
    const response = await axios.post(
      `${API_URL}/communications/click-to-call-direct`,
      payload,
      { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      const serverMessage = error.response.data?.message || error.response.data?.error;
      const err = new Error(serverMessage || 'Server error occurred');
      err.status = status;
      throw err;
    } else if (error.request) {
      throw new Error('Unable to connect to the server. Please check your internet connection.');
    } else {
      throw new Error(error.message || 'An unexpected error occurred');
    }
  }
};

// Get (lazy-generates on first call) the current user's persistent direct-call key.
export const getDirectCallKey = async () => {
  const response = await axios.get(`${API_URL}/users/me/direct-call-key`, getHeaders());
  return response.data;
};

// Rotate the current user's direct-call key. Invalidates every pre-saved URL.
export const rotateDirectCallKey = async () => {
  const response = await axios.post(`${API_URL}/users/me/direct-call-key/rotate`, {}, getHeaders());
  return response.data;
};

// Send an SMS
export const sendSms = async (toNumber, body, leadId, mediaFile = null) => {
   console.log(`Attempting to send ${mediaFile ? 'MMS' : 'SMS'} to ${toNumber} for lead ${leadId}`);
  try {
    let response;
    if (mediaFile) {
      const formData = new FormData();
      formData.append('to_number', toNumber);
      formData.append('body', body);
      if (leadId) formData.append('lead_id', leadId);
      formData.append('media', mediaFile);
      response = await axios.post(
        `${API_URL}/communications/sms`,
        formData,
        { headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'multipart/form-data' } }
      );
    } else {
      response = await axios.post(
        `${API_URL}/communications/sms`,
        { to_number: toNumber, body, lead_id: leadId },
        getHeaders()
      );
    }
    console.log('SMS API response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error sending SMS:', error);
    console.error('Error details:', error.response?.data || error.message);
    throw error;
  }
};

// Send an email to a lead using a Brevo transactional template.
// The HTML/branding lives in Brevo — we pass the template to use plus the
// rep's optional custom text, which the template renders via {{params.message}}.
export const sendEmail = async ({ leadId, emailTemplateId, message, subject, toEmail }) => {
  try {
    const response = await axios.post(
      `${API_URL}/communications/email`,
      {
        lead_id: leadId,
        email_template_id: emailTemplateId,
        message,
        subject,
        to_email: toEmail,
      },
      getHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('Error sending email:', error);
    console.error('Error details:', error.response?.data || error.message);
    throw error;
  }
};

// Get call details by ID
export const getCallDetails = async (callId) => {
  try {
    const response = await axios.get(
      `${API_URL}/communications/calls/${callId}`,
      getHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching call details:', error);
    throw error;
  }
};

// Update call notes
export const updateCallNotes = async (callId, notes) => {
  try {
    const response = await axios.put(
      `${API_URL}/communications/calls/${callId}/notes`,
      { notes },
      getHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('Error updating call notes:', error);
    throw error;
  }
};

// Get SMS details by ID
export const getSmsDetails = async (smsId) => {
  try {
    const response = await axios.get(
      `${API_URL}/communications/sms/${smsId}`,
      getHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching SMS details:', error);
    throw error;
  }
};

// NEW: Helper functions for phone number formatting and validation
/**
 * Format phone number to ensure it has the correct format
 * @param {string} phoneNumber - Raw phone number
 * @returns {string} Formatted phone number with country code
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // If it's already a full number with country code
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+${cleaned}`;
  }
  
  // If it's a 10-digit US number, add the +1 country code
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  }
  
  // If it already has a plus sign at the beginning, return as is
  if (phoneNumber.startsWith('+')) {
    return phoneNumber;
  }
  
  // For other cases, assume it needs +1
  return `+1${cleaned}`;
};

// Transcribe a call recording
export const transcribeCall = async (callId) => {
  try {
    const response = await axios.post(
      `${API_URL}/communications/calls/${callId}/transcribe`,
      {},
      getHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('Error transcribing call:', error);
    throw error;
  }
};

// Get transcription for a call
export const getTranscription = async (callId) => {
  try {
    const response = await axios.get(
      `${API_URL}/communications/calls/${callId}/transcription`,
      getHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching transcription:', error);
    throw error;
  }
};

// NEW: Validate phone number format
export const isValidPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return false;
  
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // US phone numbers: 10 digits or 11 digits starting with 1
  return cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'));
};